import React, { FC, useState } from 'react';

import { Modal } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import LockIcon from '~/assets/icons/LockIcon';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import IKImage from '~/components/IKImage/IKImage';
import JoinNowModal from '~/components/JoinNowModal/JoinNowModal';
import Ratio from '~/components/Ratio';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { BodyText, H2 } from '~/components/UI/Texts';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import VideoTitle from '~/pages/SingleVideoPage/components/VideoTitle/VideoTitle';
import {
  BREAKPOINTS_VALUES,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SKELETON_TEXT_HEIGHT_BIG_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { TENANTS_CONFIG, TenantsRefs } from '~/utils/tenantsConfig';

const SRCSET = [{ width: 467 }, { width: 767 }, { width: 641 }];

interface VideoLoggedOutPreviewProps {
  id: string;
  title?: string;
  duration: number;
  previewUrl?: string | null;
  loading?: boolean;
}

const VideoLoggedOutPreview: FC<VideoLoggedOutPreviewProps> = ({
  loading,
  id,
  title,
  duration,
  previewUrl,
}) => {
  const [openJoinModal, setOpenJoinModal] = useState(false);
  const { userInfo } = useUserInfoContext();
  const { tenant } = useTenantContext();
  const joinNowConfig = TENANTS_CONFIG[tenant?.name || TenantsRefs.Fu]?.joinNowBlock;
  const { t } = useTenantTranslation();

  const isLoggedIn = Boolean(userInfo);

  const handleOpenJoinModal = () => {
    setOpenJoinModal(true);
  };

  const handleCloseJoinModal = () => {
    setOpenJoinModal(false);
  };

  if (loading) {
    return (
      <VideoPlaceholder>
        <JoinWrapper>
          <ContentWrapper>
            <TitleWrapper>
              <AnimatedSkeleton xs={SKELETON_TEXT_HEIGHT_PX} />
            </TitleWrapper>
            <AnimatedSkeletonMultiline
              xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
              lines={3}
              spacer={TINY_MARGIN_PX}
            />
          </ContentWrapper>
          <AnimatedSkeleton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
        </JoinWrapper>
        <VideoPreview>
          <AnimatedSkeleton xs="390px" />
        </VideoPreview>
      </VideoPlaceholder>
    );
  }

  return (
    <>
      <StyledModal open={openJoinModal}>
        <JoinNowModal handleClose={handleCloseJoinModal} isLoggedIn={isLoggedIn} />
      </StyledModal>
      <VideoPlaceholder>
        <JoinWrapper>
          <ContentWrapper>
            <TitleWrapper>
              <StyledBlockLockIcon />
              <H2>{t(joinNowConfig?.titleKey)}</H2>
            </TitleWrapper>
            <Description>{t(joinNowConfig?.descriptionKey)}</Description>
          </ContentWrapper>
          <StyledLink to={joinNowConfig?.link}>
            <CustomButton>{t(joinNowConfig?.btnCaptionKey)}</CustomButton>
          </StyledLink>
        </JoinWrapper>
        <VideoPreview>
          <StyledRatio>
            <VideoPreviewImg
              srcset={SRCSET}
              sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 467px, (max-width:${BREAKPOINTS_VALUES['md']}px) 767px, 641px`}
              data-testid="previewImage"
              path={previewUrl}
            />
            <IconButton
              aria-label={t('page.video.subscribeToWatch')}
              data-testid="lockIcon"
              onClick={handleOpenJoinModal}
            >
              <StyledLockIcon />
            </IconButton>
          </StyledRatio>
          <VideoPreviewInfo>
            <VideoTitle title={title} isXApiPage={false} videoId={id} duration={duration} />
          </VideoPreviewInfo>
        </VideoPreview>
      </VideoPlaceholder>
    </>
  );
};

export default VideoLoggedOutPreview;

const VideoPlaceholder = styled('div')`
  display: flex;
  justify-content: stretch;
  flex-direction: column;
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;

const VideoPreview = styled('div')`
  width: 100%;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.common.blockBackground.light};
  position: relative;
`;

const VideoPreviewImg = styled(IKImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-width: 100%;
  border-radius: 8px 8px 0 0;
`;

const VideoPreviewInfo = styled('div')`
  padding: ${SMALL_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  border-radius: 0 0 8px 8px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    position: absolute;
    bottom: 0;
  }
`;

const StyledLockIcon = styled(LockIcon)`
  width: 24px;
  height: 24px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 40px;
    height: 40px;
  }
`;
const StyledModal = styled(Modal)`
  display: flex;
  justify-content: center;
  alight-items: center;
`;
const StyledRatio = styled(Ratio)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      bottom: -2px;
      width: 100%;
      height: 40%;
      border-radius: 0 0 8px 8px;
      background: linear-gradient(
        180deg,
        ${({ theme }) => alpha(theme.palette.common.blockBackground.light, 0)} 0%,
        ${({ theme }) => alpha(theme.palette.common.blockBackground.light, 0.776042)} 48.44%,
        ${({ theme }) => theme.palette.common.blockBackground.light} 86.98%
      );
    }
  }
`;
const IconButton = styled('button')`
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => alpha(theme.palette.common.blueDark, 0.6)};
  padding: 16px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  &:hover {
    cursor: pointer;
  }
`;

const JoinWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: ${MIDDLE_MARGIN_PX};
  padding-left: ${SUBMIDDLE_MARGIN_PX};
  background: ${({ theme }) => theme.palette.common.card.overlay.main};
  box-shadow: ${({ theme }) => theme.palette.common.card.shadowValue};
  border-radius: ${MIDDLE_RADIUS_PX};
  margin-bottom: ${MIDDLE_MARGIN_PX};
  max-width: 389px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 24px;
    margin: 0 ${SMALL_MARGIN_PX} 0 0;
  }
`;
const ContentWrapper = styled('div')`
  width: 100%;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const TitleWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${TINY_MARGIN_PX};
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;
const StyledBlockLockIcon = styled(LockIcon)`
  width: 24px;
  height: 24px;
  margin-right: ${TINY_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 60px;
    height: 60px;
  }
`;
const StyledLink = styled(Link)`
  margin-left: 32px;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: 0;
  }
`;
const Description = styled(BodyText)`
  margin-left: 32px;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: 0;
  }
`;
